import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { PiDevices } from 'react-icons/pi';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth';
import EquipmentsNew from './EquipmentsNew';
import CustomModal from '../../components/CustomModal/CustomModal';
import EquipmentsEdit from './EquipmentsEdit';

type EquipmentModel = {
  id: number;
  name: string;
  brand: string;
  model: string;
  serialNumber: string;
  description: string;
};

interface EquipmentsIndexProps {
  customerId: number;
}

export default function EquipmentsIndex({ customerId }: EquipmentsIndexProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [equipmentId, setEquipmentId] = useState<number>(0);
  const [equipments, setEquipments] = useState([] as EquipmentModel[]);

  const {
    user: { organization },
  } = useAuth();

  const addEquipment = () => {
    setIsOpenModal(true);
    setIsUpdate(false);
  };

  const editEquipment = (id: number) => {
    setIsOpenModal(true);
    setIsUpdate(true);
    setEquipmentId(id);
  };

  const getEquipments = async () => {
    if (organization && customerId) {
      setIsLoading(true);
      const response = await api.get(
        `organizations/${organization.id}/customers/${customerId}/equipments`
      );
      setEquipments(response.data.data);
      setIsLoading(false);
    }
  };

  const removeEquipment = useCallback(
    async (equipment: EquipmentModel) => {
      try {
        const displayName = `${equipment.name}`;
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o equipamento "${displayName}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/customers/${customerId}/equipments/${equipment.id}`
          );
          getEquipments();
          toast.success(
            `O equipamento "${displayName}" foi removido do Cliente`
          );
        }
      } catch (e) {
        toast.error(
          'Houve um problema ao tentar remover o equipamento do Cliente. Tente novamente.'
        );
      }
    },
    [organization.id, customerId, getEquipments, toast]
  );

  useEffect(() => {
    getEquipments();
  }, [organization, customerId]);

  return (
    <>
      <div className='table-responsive'>
        <table className='table table-hover'>
          <thead>
            <tr>
              <th scope='col'>#</th>
              <th scope='col'>Nome</th>
              <th scope='col'>Marca</th>
              <th scope='col'>Modelo</th>
              <th scope='col'>N/S</th>
              <th>Observações</th>
              <th style={{ width: '20px' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {equipments.map((i) => {
              return (
                <tr key={i.id}>
                  <th scope='row'>{i.id}</th>
                  <td>
                    <Link
                      className='nav-link active p-0 text-black'
                      to='/'
                      role='button'
                      data-bs-toggle='button'
                      onClick={() => editEquipment(i.id)}
                    >
                      {i.name}
                    </Link>
                  </td>
                  <td>{i.brand}</td>
                  <td>{i.model}</td>
                  <td>{i.serialNumber}</td>
                  <td>
                    <div className='d-flex align-content-center gap-2'>
                      {i.description ? (
                        <div>{i.description}</div>
                      ) : (
                        <i>Sem observação</i>
                      )}
                    </div>
                  </td>
                  <td>
                    <button
                      className='btn btn-sm btn-danger'
                      type='button'
                      onClick={() => removeEquipment(i)}
                    >
                      <FaTimes />
                    </button>
                  </td>
                </tr>
              );
            })}
            {equipments.length === 0 && (
              <tr>
                <td colSpan={6}>Nenhum registro encontrado</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='text-end mt-2'>
        <button
          type='button'
          className='btn btn-secondary btn-sm'
          onClick={() => addEquipment()}
        >
          Adicionar equipamento <PiDevices />
        </button>
      </div>
      <CustomModal
        isOpen={isOpenModal}
        modalTitle={isUpdate ? 'Editar Equipamento' : 'Novo Equipamento'}
        setIsOpenModal={setIsOpenModal}
        onCloseModal={getEquipments}
      >
        {isUpdate ? (
          <EquipmentsEdit
            id={equipmentId}
            customerId={customerId}
            setIsOpenModal={setIsOpenModal}
          />
        ) : (
          <EquipmentsNew
            customerId={customerId}
            setIsOpenModal={setIsOpenModal}
          />
        )}
      </CustomModal>
    </>
  );
}
